import { useState, useEffect } from 'react';

export interface SidebarPermissions {
  [key: string]: boolean;
}

export interface OrgNavData {
  account: { id: number; name: string };
  accounts: { id: number; name: string }[];
  publication: { id: number; name: string };
  publications: { id: number; name: string }[];
  /**
   * Org-level "All publications" target for the requesting settings surface,
   * or null when the surface has no "All" entry (or the user fails its gate).
   * Server-authoritative — mirrors classic's `show_all_publications_link?`.
   */
  all_publications_path: string | null;
  sidebar_permissions: SidebarPermissions;
}

/**
 * @param publicationId publication scope for the nav data
 * @param surface optional settings surface ('integrations' | 'content_portals'
 *   | 'tag_groups' | 'users'). When set, the BE returns the classic per-surface
 *   publication list + the gated "All publications" path. Omitted on non-settings
 *   surfaces (Media/Talent/etc.) → default staffed-only list, no "All".
 */
export default function useOrgNav(
  publicationId: string | undefined,
  surface?: string,
): OrgNavData | null {
  const [data, setData] = useState<OrgNavData | null>(null);

  useEffect(() => {
    if (!publicationId) return;

    const query = surface ? `?surface=${encodeURIComponent(surface)}` : '';
    fetch(`/publications/${publicationId}/org_nav_data.json${query}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(res => { if (!res.ok) throw new Error('Failed'); return res.json(); })
      .then(setData)
      .catch(() => { /* silently fail */ });
  }, [publicationId, surface]);

  return data;
}
